import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Stethoscope, AlertTriangle, CheckCircle, X, Edit2, RefreshCw, FileText,
  ShieldAlert, Activity, BookOpen, ChevronDown, ChevronUp, Clock,
  Brain, FlaskConical, Pill, Sparkles,
} from 'lucide-react';
import {
  Badge, btnPrimary, btnSm, inp, neuShadow, neuShadowSm, cardWrap, emptyMsg,
  overlayStyle, modalStyle, modalHeader, modalTitle, closeBtn,
} from './crmShared';

/* ═══════════════════════════════════════════════════════════════════════════
   ClinicalAITab — Phase 1: Treatment Protocol Matching.

   Physician-only tool. Generates treatment protocol suggestions via Opus 4.7
   on Bedrock, with citations + confidence + concerns. Every suggestion
   requires explicit physician review (accept / accept-with-edits / reject /
   needs-more-info) — nothing influences the chart without that decision.

   This UI implements the 21st Century Cures Act CDS exemption posture:
   physician initiates, reviews, makes final call. The AI is research aid,
   not decision-maker.
   ═══════════════════════════════════════════════════════════════════════════ */

const ROLES_ALLOWED = ['physician', 'admin', 'sysadmin'];   // only these see this tab content
const ROLES_VIEW = [...ROLES_ALLOWED, 'nurse'];               // nurses can view but not review

// Phase 2: 4 distinct clinical-AI request kinds. Each has its own input form
// + result rendering. Backend stores all 4 in crm_clinical_ai_requests with
// the request_type column. Frontend selects which generator modal to open.
const KINDS = [
  { id: 'protocol',     label: 'Protocol Match',       icon: Activity,      color: '#2E8B98',
    api: 'clinicalAiMatchProtocols',  requestType: 'protocol_match',
    blurb: 'Suggest regenerative-medicine protocols (stem cell, NK cell, exosome, peptide) for a presentation.' },
  { id: 'differential', label: 'Differential Diagnosis', icon: Brain,       color: '#A855F7',
    api: 'clinicalAiDifferentialDx',  requestType: 'differential_dx',
    blurb: 'Generate a differential diagnosis from symptoms + exam findings, with ICD-10 codes and red-flag triggers.' },
  { id: 'lab',          label: 'Lab / Imaging Summary', icon: FlaskConical, color: '#10B981',
    api: 'clinicalAiLabSummary',      requestType: 'lab_summary',
    blurb: 'Summarize lab or imaging results, flag abnormal/emergent values, identify patterns.' },
  { id: 'drug',         label: 'Drug Interaction Check', icon: Pill,        color: '#F59E0B',
    api: 'clinicalAiDrugInteraction', requestType: 'drug_interaction',
    blurb: 'Check a proposed regimen against current meds + allergies. Flag contraindications, suggest alternatives.' },
];

const KIND_BY_REQUEST_TYPE = Object.fromEntries(KINDS.map(k => [k.requestType, k]));

export default function ClinicalAITab({ contactId, contactName, sessionRole, sessionUserId }) {
  // Resolve session role locally if parent didn't pass it. Falls back to
  // user:profile:get-self (already exposed via preload as getOwnProfile).
  // sessionUserId is accepted as a prop for future use but not required.
  const [resolvedRole, setResolvedRole] = useState(sessionRole || null);

  useEffect(() => {
    if (sessionRole) return;
    let cancelled = false;
    (async () => {
      try {
        const me = await window.zignagenixAPI.getOwnProfile();
        if (cancelled || !me) return;
        setResolvedRole(me.role || 'unknown');
      } catch {
        if (!cancelled) setResolvedRole('unknown');
      }
    })();
    return () => { cancelled = true; };
  }, [sessionRole]);

  const allowedToView = ROLES_VIEW.includes(resolvedRole);
  const allowedToReview = ROLES_ALLOWED.includes(resolvedRole);

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  // showGenerator now holds the kind id ('protocol' | 'differential' | 'lab' | 'drug') or null.
  const [showGenerator, setShowGenerator] = useState(null);
  const [activeRequest, setActiveRequest] = useState(null);

  const reload = useCallback(async () => {
    if (!contactId || !allowedToView) { setLoading(false); return; }
    setLoading(true);
    try {
      const r = await window.zignagenixAPI.listClinicalAiRequests({ contactId });
      setRequests(Array.isArray(r) ? r : []);
    } finally { setLoading(false); }
  }, [contactId, allowedToView]);

  useEffect(() => { reload(); }, [reload]);

  if (!allowedToView) {
    return (
      <div style={{ padding: 32, textAlign: 'center' }}>
        <ShieldAlert size={32} color="#94A3B8" style={{ margin: '0 auto 12px' }} />
        <div style={{ fontSize: 14, fontWeight: 700, color: '#475569', marginBottom: 6 }}>
          Clinical AI is restricted
        </div>
        <div style={{ fontSize: 12, color: '#94A3B8', maxWidth: 360, margin: '0 auto' }}>
          Only users with role physician, nurse, or admin can view this tab. Contact your administrator if you need access.
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 16 }}>
      {/* Compliance banner — always visible */}
      <div style={{
        background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: 10,
        padding: '10px 14px', marginBottom: 16,
        display: 'flex', alignItems: 'flex-start', gap: 10,
      }}>
        <AlertTriangle size={18} color="#B45309" style={{ flexShrink: 0, marginTop: 2 }} />
        <div style={{ fontSize: 12, color: '#7C2D12', lineHeight: 1.5 }}>
          <strong>AI-generated suggestions — physician review required.</strong> All
          recommendations from this tool must be independently verified and explicitly
          reviewed by a licensed physician before any clinical decision. The AI is a
          research aid, not a decision-maker. Output is logged with the reviewing
          physician's identity and NPI for compliance.
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <Stethoscope size={18} color="#2E8B98" />
          <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: '#212529' }}>
            Clinical AI Decision Support
          </h2>
        </div>
        {allowedToReview && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8 }}>
            {KINDS.map(k => {
              const Icon = k.icon;
              return (
                <button key={k.id} onClick={() => setShowGenerator(k.id)} style={{
                  padding: '10px 12px', borderRadius: 10,
                  border: `1px solid ${k.color}55`,
                  background: '#FFFFFF', textAlign: 'left',
                  cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 4,
                  transition: 'transform .1s, box-shadow .1s',
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = `0 2px 8px ${k.color}22`; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Icon size={14} color={k.color} />
                    <span style={{ fontSize: 12, fontWeight: 700, color: k.color }}>{k.label}</span>
                    <Sparkles size={10} color={k.color} style={{ marginLeft: 'auto', opacity: 0.6 }} />
                  </div>
                  <span style={{ fontSize: 10, color: '#64748B', lineHeight: 1.3 }}>{k.blurb}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {loading ? (
        <div style={emptyMsg}>Loading…</div>
      ) : requests.length === 0 ? (
        <div style={{ ...cardWrap, padding: 24, textAlign: 'center' }}>
          <div style={{ fontSize: 13, color: '#475569', marginBottom: 8 }}>
            No clinical AI requests for this patient yet.
          </div>
          <div style={{ fontSize: 11, color: '#94A3B8', maxWidth: 480, margin: '0 auto 14px' }}>
            Click any of the 4 modules above to generate a draft for physician review.
            Every request is logged with full audit trail and physician-decision capture.
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {requests.map(r => (
            <RequestRow key={r.id} request={r} onOpen={() => setActiveRequest(r.id)} />
          ))}
        </div>
      )}

      {showGenerator && (
        <ClinicalAIGenerator
          kind={KINDS.find(k => k.id === showGenerator)}
          contactId={contactId}
          contactName={contactName}
          onClose={() => setShowGenerator(null)}
          onGenerated={(requestId) => {
            setShowGenerator(null);
            setActiveRequest(requestId);
            reload();
          }}
        />
      )}

      {activeRequest !== null && (
        <RequestDetailModal
          requestId={activeRequest}
          allowedToReview={allowedToReview}
          onClose={() => { setActiveRequest(null); reload(); }}
        />
      )}
    </div>
  );
}

/* ─── Request row in the history list ──────────────────────────────────────── */

function RequestRow({ request, onOpen }) {
  const statusBadge = {
    processing: { color: '#1E40AF', bg: '#DBEAFE', label: 'PROCESSING…' },
    pending_review: { color: '#B45309', bg: '#FEF3C7', label: 'PENDING REVIEW' },
    reviewed: { color: '#15803D', bg: '#DCFCE7', label: 'REVIEWED' },
    error: { color: '#DC2626', bg: '#FEE2E2', label: 'ERROR' },
  }[request.status] || { color: '#475569', bg: '#F1F5F9', label: request.status?.toUpperCase() };
  const actionBadge = request.latest_action ? {
    accepted: { color: '#15803D', bg: '#DCFCE7', label: 'ACCEPTED' },
    accepted_with_edits: { color: '#0F766E', bg: '#CCFBF1', label: 'ACCEPTED W/ EDITS' },
    rejected: { color: '#DC2626', bg: '#FEE2E2', label: 'REJECTED' },
    needs_more_info: { color: '#7C3AED', bg: '#F3E8FF', label: 'NEEDS MORE INFO' },
  }[request.latest_action] : null;

  return (
    <div onClick={onOpen} style={{
      ...cardWrap, padding: 12, cursor: 'pointer',
      transition: 'transform .1s ease',
    }}
    onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-1px)'}
    onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <BookOpen size={14} color="#2E8B98" />
        <span style={{ fontSize: 13, fontWeight: 700, color: '#212529' }}>
          {request.request_type === 'protocol_match' ? 'Protocol matching' : request.request_type}
        </span>
        <span style={{ ...badgeStyle(statusBadge.color, statusBadge.bg) }}>{statusBadge.label}</span>
        {actionBadge && <span style={badgeStyle(actionBadge.color, actionBadge.bg)}>{actionBadge.label}</span>}
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 10, color: '#94A3B8' }}>
          <Clock size={10} style={{ display: 'inline', marginRight: 3 }} />
          {new Date(request.created_at).toLocaleString()}
        </span>
      </div>
      <div style={{ fontSize: 11, color: '#6C757D', display: 'flex', gap: 12 }}>
        <span>Requested by <strong>{request.requested_by_name}</strong></span>
        {request.review_count > 0 && <span>· {request.review_count} review{request.review_count !== 1 ? 's' : ''}</span>}
        {request.input_tokens && <span>· {request.input_tokens + (request.output_tokens || 0)} tokens</span>}
        {request.latency_ms && <span>· {(request.latency_ms / 1000).toFixed(1)}s</span>}
        {request.error_message && (
          <span style={{ color: '#DC2626', fontWeight: 600 }}>· error</span>
        )}
      </div>
    </div>
  );
}

/* ─── Generator modal — adaptive for all 4 kinds ──────────────────────────── */

// Field schemas per kind. Each generator collects different inputs but the
// shared modal renders them via a simple field array.
const FIELD_SCHEMAS = {
  protocol: {
    title: 'Generate Protocol Suggestions',
    fields: [
      { key: 'chiefComplaint', label: 'Chief complaint *', required: true, rows: 3,
        placeholder: "e.g. 'chronic right knee pain post-ACL repair 2 yrs ago, VAS 7/10, MRI shows partial medial meniscus tear'." },
      { key: 'additionalContext', label: 'Additional context (optional)', rows: 3,
        placeholder: 'Prior treatments tried, patient preferences, specific protocols you are considering, etc.' },
    ],
  },
  differential: {
    title: 'Generate Differential Diagnosis',
    fields: [
      { key: 'symptoms', label: 'Presenting symptoms *', required: true, rows: 4,
        placeholder: 'Onset, character, severity, location, aggravating/relieving factors. E.g. "3 weeks progressive right shoulder pain, sharp with overhead motion, VAS 7/10, no trauma, no radiation".' },
      { key: 'examFindings', label: 'Physical exam findings', rows: 3,
        placeholder: 'Vitals, focused exam findings. E.g. "ROM limited at 110° abduction, positive Hawkins-Kennedy, negative drop-arm".' },
      { key: 'relevantLabsImaging', label: 'Relevant labs / imaging (optional, supplements chart)', rows: 3,
        placeholder: 'Anything not yet in the chart that the physician considers relevant.' },
      { key: 'additionalContext', label: 'Additional context', rows: 2,
        placeholder: 'Anything else the physician wants the AI to consider.' },
    ],
  },
  lab: {
    title: 'Summarize Lab / Imaging Results',
    fields: [
      { key: 'labResults', label: 'Lab / imaging results to summarize *', required: true, rows: 8,
        placeholder: 'Paste the panel as you have it. E.g.\n  WBC 14.2 (4.0-11.0)\n  Hgb 9.8 (13.5-17.5)\n  Plt 380 (150-450)\n  Cr 1.8 (0.7-1.3)\n  ...' },
      { key: 'clinicalQuestion', label: 'Specific question for the AI (optional)', rows: 2,
        placeholder: 'E.g. "is this consistent with iron deficiency?" or "what would I expect on follow-up labs?"' },
      { key: 'additionalContext', label: 'Additional context', rows: 2,
        placeholder: 'Recent illness, new symptoms, medication changes, etc.' },
    ],
  },
  drug: {
    title: 'Drug Interaction Check',
    fields: [
      { key: 'proposedRegimen', label: 'Proposed new regimen *', required: true, rows: 4,
        placeholder: 'Each drug on its own line. E.g.\n  Methotrexate 15 mg PO weekly\n  Folic acid 1 mg PO daily\n  Ibuprofen 400 mg PO Q6H prn' },
      { key: 'clinicalIndication', label: 'Clinical indication', rows: 2,
        placeholder: 'What is this regimen treating? E.g. "RA flare unresponsive to NSAIDs alone".' },
      { key: 'additionalContext', label: 'Additional context', rows: 2,
        placeholder: 'Patient preferences, prior trials, kidney/liver function specifics, etc.' },
    ],
  },
};

function ClinicalAIGenerator({ kind, contactId, contactName, onClose, onGenerated }) {
  const schema = FIELD_SCHEMAS[kind.id];
  const Icon = kind.icon;
  const [values, setValues] = useState(() => Object.fromEntries(schema.fields.map(f => [f.key, ''])));
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  function setField(key, val) {
    setValues(prev => ({ ...prev, [key]: val }));
  }

  async function generate() {
    // Validate required fields
    for (const f of schema.fields) {
      if (f.required && !values[f.key].trim()) {
        setError(`${f.label.replace(' *', '')} is required`);
        return;
      }
    }
    setGenerating(true);
    setError(null);
    try {
      // Build payload — trim every field, drop empties
      const payload = { contactId };
      for (const f of schema.fields) {
        const v = values[f.key].trim();
        if (v) payload[f.key] = v;
      }
      const r = await window.zignagenixAPI[kind.api](payload);
      if (!r || r.success === false) {
        setError(r?.error || 'Generation failed');
        return;
      }
      onGenerated(r.requestId);
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  }

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={{ ...modalStyle, width: 680 }} onClick={e => e.stopPropagation()}>
        <div style={modalHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon size={16} color={kind.color} />
            <h2 style={modalTitle}>{schema.title}</h2>
          </div>
          <button onClick={onClose} style={closeBtn}><X size={18} color="#ADB5BD" /></button>
        </div>

        <div style={{
          padding: '8px 12px', borderRadius: 8, marginBottom: 14,
          background: '#FEF3C7', display: 'flex', alignItems: 'flex-start', gap: 8,
          fontSize: 11, color: '#7C2D12',
        }}>
          <AlertTriangle size={13} color="#B45309" style={{ marginTop: 1, flexShrink: 0 }} />
          <div>
            Patient context will be auto-loaded from this contact&apos;s chart
            (encounters, medications, allergies, recent labs, prior treatments).
            The AI does NOT make decisions — every suggestion requires physician review.
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <Lbl>Patient</Lbl>
          <input value={contactName || `Contact #${contactId}`} disabled
            style={{ ...inp, background: '#F1F5F9', color: '#475569' }} />
        </div>

        {schema.fields.map(f => (
          <div key={f.key} style={{ marginBottom: 12 }}>
            <Lbl>{f.label}</Lbl>
            <textarea
              value={values[f.key]}
              onChange={e => setField(f.key, e.target.value)}
              rows={f.rows || 3}
              placeholder={f.placeholder}
              style={{ ...inp, resize: 'vertical' }}
            />
          </div>
        ))}

        {error && (
          <div style={{
            padding: '8px 12px', borderRadius: 8, marginBottom: 12,
            background: '#FEE2E2', color: '#991B1B', fontSize: 12,
          }}>
            ✗ {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={btnSm}>Cancel</button>
          <button onClick={generate} disabled={generating}
            style={{ ...btnPrimary, background: kind.color, borderColor: kind.color }}>
            {generating ? (
              <><RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} /> Submitting…</>
            ) : (
              <><Icon size={13} /> Generate</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Detail modal — view + review ─────────────────────────────────────────── */

function RequestDetailModal({ requestId, allowedToReview, onClose }) {
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [pollElapsed, setPollElapsed] = useState(0);

  const reload = useCallback(async () => {
    try {
      const r = await window.zignagenixAPI.getClinicalAiRequest(requestId);
      setRequest(r);
      return r;
    } finally { setLoading(false); }
  }, [requestId]);

  // Initial fetch + polling while status='processing'.
  // Hard cap at 3 minutes — if Bedrock hasn't finished by then something
  // is genuinely wrong and we stop hammering the server.
  useEffect(() => {
    let cancelled = false;
    let timer = null;
    const startedAt = Date.now();
    const HARD_CAP_MS = 180_000;

    async function tick() {
      if (cancelled) return;
      const elapsed = Date.now() - startedAt;
      setPollElapsed(Math.floor(elapsed / 1000));
      const r = await reload();
      if (cancelled) return;
      if (!r || r.error) return; // stop on hard error
      if (r.status === 'processing' && elapsed < HARD_CAP_MS) {
        timer = setTimeout(tick, 2500);
      }
    }
    tick();

    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [reload]);

  if (loading && !request) {
    return (
      <div style={overlayStyle} onClick={onClose}>
        <div style={modalStyle} onClick={e => e.stopPropagation()}>
          <div style={{ padding: 24, textAlign: 'center' }}>Loading…</div>
        </div>
      </div>
    );
  }
  if (!request || request.error) {
    return (
      <div style={overlayStyle} onClick={onClose}>
        <div style={modalStyle} onClick={e => e.stopPropagation()}>
          <div style={{ padding: 24, textAlign: 'center', color: '#DC2626' }}>Request not found</div>
        </div>
      </div>
    );
  }

  // Processing state — show a "generating" panel that auto-updates as the
  // backend finishes the Bedrock call.
  if (request.status === 'processing') {
    return (
      <div style={overlayStyle} onClick={onClose}>
        <div style={{ ...modalStyle, width: 560 }} onClick={e => e.stopPropagation()}>
          <div style={modalHeader}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Stethoscope size={16} color="#2E8B98" />
              <h2 style={modalTitle}>Generating protocol suggestions…</h2>
            </div>
            <button onClick={onClose} style={closeBtn}><X size={18} color="#ADB5BD" /></button>
          </div>
          <div style={{
            padding: 20, textAlign: 'center', color: '#475569',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
          }}>
            <RefreshCw size={28} color="#2E8B98" style={{ animation: 'spin 1s linear infinite' }} />
            <div style={{ fontSize: 13 }}>
              Opus 4.6 is analyzing the patient context and drafting suggestions.
            </div>
            <div style={{ fontSize: 11, color: '#94A3B8' }}>
              Typical: 30–90 seconds · Elapsed: {pollElapsed}s
            </div>
            <div style={{ fontSize: 10, color: '#94A3B8', maxWidth: 380, lineHeight: 1.5 }}>
              You can safely close this window — generation continues in the background and
              the result will appear in the Clinical AI tab history when ready.
            </div>
          </div>
        </div>
      </div>
    );
  }

  const parsed = request.parsed_response;
  const reviews = request.reviews || [];
  const requestKind = KIND_BY_REQUEST_TYPE[request.request_type] || KINDS[0];
  const KindIcon = requestKind.icon;

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={{ ...modalStyle, width: 820, maxHeight: '92vh' }} onClick={e => e.stopPropagation()}>
        <div style={modalHeader}>
          <div>
            <h2 style={{ ...modalTitle, display: 'flex', alignItems: 'center', gap: 8 }}>
              <KindIcon size={16} color={requestKind.color} />
              {requestKind.label} — {request.first_name} {request.last_name}
            </h2>
            <div style={{ fontSize: 11, color: '#6C757D', marginTop: 4 }}>
              Generated by <strong>{request.requested_by_name}</strong> on {new Date(request.created_at).toLocaleString()}
              {' · '}Model: <code style={{ fontSize: 10 }}>{request.model_id}</code>
              {request.input_tokens && <> · {request.input_tokens + (request.output_tokens || 0)} tokens</>}
              {request.latency_ms && <> · {(request.latency_ms / 1000).toFixed(1)}s</>}
            </div>
          </div>
          <button onClick={onClose} style={closeBtn}><X size={18} color="#ADB5BD" /></button>
        </div>

        {/* Compliance banner */}
        <div style={{
          background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: 8,
          padding: '8px 12px', marginBottom: 14,
          display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 11, color: '#7C2D12',
        }}>
          <AlertTriangle size={13} color="#B45309" style={{ marginTop: 1, flexShrink: 0 }} />
          <span>AI-generated suggestion. Physician review required before any clinical action. Cite sources independently.</span>
        </div>

        {request.error_message && (
          <div style={{
            background: '#FEE2E2', border: '1px solid #DC2626', borderRadius: 8,
            padding: 12, marginBottom: 14, fontSize: 12, color: '#991B1B',
          }}>
            <strong>Generation failed:</strong> {request.error_message}
          </div>
        )}

        {parsed?.summary && (
          <div style={{ ...cardWrap, padding: 12, marginBottom: 12, background: '#F8FAFC' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#475569', textTransform: 'uppercase', marginBottom: 6 }}>
              Summary
            </div>
            <div style={{ fontSize: 13, color: '#0F172A', lineHeight: 1.5 }}>{parsed.summary}</div>
          </div>
        )}

        {/* ─── Type-specific main item list ──────────────────────────────── */}
        {request.request_type === 'protocol_match' && Array.isArray(parsed?.suggestions) &&
          parsed.suggestions.map((s, i) => <SuggestionCard key={i} suggestion={s} index={i} />)
        }
        {request.request_type === 'differential_dx' && Array.isArray(parsed?.differentials) &&
          parsed.differentials.map((d, i) => <DifferentialCard key={i} differential={d} index={i} />)
        }
        {request.request_type === 'lab_summary' && Array.isArray(parsed?.abnormal_findings) &&
          parsed.abnormal_findings.map((f, i) => <AbnormalFindingCard key={i} finding={f} index={i} />)
        }
        {request.request_type === 'drug_interaction' && Array.isArray(parsed?.interactions) &&
          parsed.interactions.map((x, i) => <InteractionCard key={i} interaction={x} index={i} />)
        }

        {/* Type-specific high-priority sections */}
        {request.request_type === 'differential_dx' && Array.isArray(parsed?.red_flags) && parsed.red_flags.length > 0 && (
          <RedListCard title="Red Flags — escalate workup" items={parsed.red_flags} />
        )}
        {request.request_type === 'lab_summary' && Array.isArray(parsed?.emergent_values) && parsed.emergent_values.length > 0 && (
          <RedListCard title="Emergent Values — physician attention required" items={parsed.emergent_values} />
        )}
        {request.request_type === 'drug_interaction' && Array.isArray(parsed?.contraindications) && parsed.contraindications.length > 0 && (
          <RedListCard title="Contraindications" items={parsed.contraindications} />
        )}

        {/* Type-specific informational sections */}
        {request.request_type === 'lab_summary' && Array.isArray(parsed?.patterns_identified) && parsed.patterns_identified.length > 0 && (
          <InfoListCard title="Patterns identified" items={parsed.patterns_identified} />
        )}
        {request.request_type === 'lab_summary' && Array.isArray(parsed?.follow_up_tests_suggested) && parsed.follow_up_tests_suggested.length > 0 && (
          <InfoListCard title="Follow-up tests suggested" items={parsed.follow_up_tests_suggested} />
        )}
        {request.request_type === 'drug_interaction' && Array.isArray(parsed?.alternative_suggestions) && parsed.alternative_suggestions.length > 0 && (
          <div style={{ ...cardWrap, padding: 12, marginBottom: 12, background: '#F0FDFA', border: '1px solid #14B8A6' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#0F766E', marginBottom: 6 }}>
              Alternative suggestions
            </div>
            {parsed.alternative_suggestions.map((alt, i) => (
              <div key={i} style={{ fontSize: 12, color: '#134E4A', marginBottom: 4, lineHeight: 1.5 }}>
                <strong>Instead of {alt.instead_of}, consider {alt.consider}:</strong> {alt.rationale}
              </div>
            ))}
          </div>
        )}

        {/* Missing info */}
        {Array.isArray(parsed?.missing_information) && parsed.missing_information.length > 0 && (
          <div style={{
            ...cardWrap, padding: 12, marginBottom: 12,
            background: '#FFFBEB', border: '1px solid #FBBF24',
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#92400E', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 5 }}>
              <FileText size={12} /> Missing information
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: '#78350F' }}>
              {parsed.missing_information.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          </div>
        )}

        {/* Do not proceed (or do_not_delay_if for differential) */}
        {Array.isArray(parsed?.do_not_proceed_if) && parsed.do_not_proceed_if.length > 0 && (
          <div style={{
            ...cardWrap, padding: 12, marginBottom: 14,
            background: '#FEF2F2', border: '1px solid #DC2626',
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#991B1B', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 5 }}>
              <ShieldAlert size={12} /> Do NOT proceed if
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: '#7F1D1D' }}>
              {parsed.do_not_proceed_if.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          </div>
        )}
        {Array.isArray(parsed?.do_not_delay_if) && parsed.do_not_delay_if.length > 0 && (
          <div style={{
            ...cardWrap, padding: 12, marginBottom: 14,
            background: '#FEF2F2', border: '1px solid #DC2626',
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#991B1B', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 5 }}>
              <ShieldAlert size={12} /> Do NOT delay referral if
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: '#7F1D1D' }}>
              {parsed.do_not_delay_if.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          </div>
        )}

        {/* Reviews so far */}
        {reviews.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 8 }}>
              Physician reviews
            </div>
            {reviews.map(rv => (
              <ReviewRow key={rv.id} review={rv} />
            ))}
          </div>
        )}

        {/* Review action */}
        {allowedToReview && !showReviewForm && (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={onClose} style={btnSm}>Close</button>
            <button onClick={() => setShowReviewForm(true)} style={btnPrimary}>
              <CheckCircle size={13} /> Add physician review
            </button>
          </div>
        )}

        {showReviewForm && (
          <ReviewForm
            requestId={requestId}
            onSubmitted={() => { setShowReviewForm(false); reload(); }}
            onCancel={() => setShowReviewForm(false)}
          />
        )}
      </div>
    </div>
  );
}

function SuggestionCard({ suggestion, index }) {
  const [expanded, setExpanded] = useState(true);
  const conf = suggestion.confidence || 'unknown';
  const confColor = conf === 'high' ? '#15803D' : conf === 'moderate' ? '#B45309' : conf === 'low' ? '#94A3B8' : '#475569';

  return (
    <div style={{ ...cardWrap, padding: 12, marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, cursor: 'pointer' }}
        onClick={() => setExpanded(!expanded)}>
        <Badge text={`#${index + 1}`} color="#2E8B98" />
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0F172A', flex: 1 }}>
          {suggestion.protocol_type?.replace(/_/g, ' ')} — {suggestion.product_name || '(unspecified)'}
        </div>
        <span style={{ ...badgeStyle(confColor, confColor + '20'), textTransform: 'uppercase' }}>
          {conf} conf
        </span>
        {expanded ? <ChevronUp size={14} color="#94A3B8" /> : <ChevronDown size={14} color="#94A3B8" />}
      </div>

      {expanded && (
        <>
          {suggestion.rationale && (
            <Field label="Rationale">{suggestion.rationale}</Field>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 8 }}>
            {suggestion.dosage_range && <Field label="Dosage">{suggestion.dosage_range}</Field>}
            {suggestion.route && <Field label="Route">{suggestion.route}</Field>}
            {suggestion.frequency && <Field label="Frequency">{suggestion.frequency}</Field>}
          </div>
          {suggestion.confidence_rationale && (
            <Field label="Why this confidence">{suggestion.confidence_rationale}</Field>
          )}
          {Array.isArray(suggestion.supporting_sources) && suggestion.supporting_sources.length > 0 && (
            <div style={{ marginBottom: 6 }}>
              <FieldLbl>Sources cited (verify independently)</FieldLbl>
              <ul style={{ margin: '4px 0 0 18px', padding: 0, fontSize: 11, color: '#0F172A' }}>
                {suggestion.supporting_sources.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </div>
          )}
          {Array.isArray(suggestion.concerns_to_verify) && suggestion.concerns_to_verify.length > 0 && (
            <div style={{ marginBottom: 6 }}>
              <FieldLbl>Concerns to verify</FieldLbl>
              <ul style={{ margin: '4px 0 0 18px', padding: 0, fontSize: 11, color: '#7C2D12' }}>
                {suggestion.concerns_to_verify.map((c, i) => <li key={i}>{c}</li>)}
              </ul>
            </div>
          )}
          {Array.isArray(suggestion.contraindications) && suggestion.contraindications.length > 0 && (
            <div style={{ marginBottom: 6 }}>
              <FieldLbl>Contraindications</FieldLbl>
              <ul style={{ margin: '4px 0 0 18px', padding: 0, fontSize: 11, color: '#991B1B' }}>
                {suggestion.contraindications.map((c, i) => <li key={i}>{c}</li>)}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ─── Differential card (Phase 2a) ──────────────────────────────────────── */

function DifferentialCard({ differential, index }) {
  const [expanded, setExpanded] = useState(true);
  const lk = differential.likelihood || 'unknown';
  const lkColor = lk === 'high' ? '#15803D' : lk === 'moderate' ? '#B45309' : lk === 'low' ? '#94A3B8' : '#475569';

  return (
    <div style={{ ...cardWrap, padding: 12, marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, cursor: 'pointer' }}
        onClick={() => setExpanded(!expanded)}>
        <Badge text={`#${index + 1}`} color="#A855F7" />
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0F172A', flex: 1 }}>
          {differential.diagnosis} {differential.icd10 && <span style={{ fontSize: 10, color: '#94A3B8', fontWeight: 500 }}>({differential.icd10})</span>}
        </div>
        <span style={{ ...badgeStyle(lkColor, lkColor + '20'), textTransform: 'uppercase' }}>
          {lk} likelihood
        </span>
        {expanded ? <ChevronUp size={14} color="#94A3B8" /> : <ChevronDown size={14} color="#94A3B8" />}
      </div>
      {expanded && (
        <>
          {differential.rationale && <Field label="Rationale">{differential.rationale}</Field>}
          {Array.isArray(differential.supporting_evidence) && differential.supporting_evidence.length > 0 && (
            <div style={{ marginBottom: 6 }}>
              <FieldLbl>Supporting evidence</FieldLbl>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: '#0F172A' }}>
                {differential.supporting_evidence.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </div>
          )}
          {Array.isArray(differential.opposing_evidence) && differential.opposing_evidence.length > 0 && (
            <div style={{ marginBottom: 6 }}>
              <FieldLbl>Opposing evidence</FieldLbl>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: '#475569' }}>
                {differential.opposing_evidence.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </div>
          )}
          {Array.isArray(differential.next_steps_to_confirm) && differential.next_steps_to_confirm.length > 0 && (
            <div style={{ marginBottom: 6 }}>
              <FieldLbl>Next steps to confirm</FieldLbl>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: '#0F172A' }}>
                {differential.next_steps_to_confirm.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </div>
          )}
          {Array.isArray(differential.supporting_sources) && differential.supporting_sources.length > 0 && (
            <div>
              <FieldLbl>Sources cited (verify independently)</FieldLbl>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 11, color: '#475569' }}>
                {differential.supporting_sources.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ─── Abnormal lab finding card (Phase 2b) ──────────────────────────────── */

function AbnormalFindingCard({ finding, index }) {
  const [expanded, setExpanded] = useState(true);
  const sev = finding.severity || 'unknown';
  const sevColor = sev === 'critical' ? '#DC2626' : sev === 'abnormal' ? '#F59E0B' : sev === 'borderline' ? '#94A3B8' : '#475569';

  return (
    <div style={{ ...cardWrap, padding: 12, marginBottom: 10, borderLeft: `3px solid ${sevColor}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, cursor: 'pointer' }}
        onClick={() => setExpanded(!expanded)}>
        <Badge text={`#${index + 1}`} color="#10B981" />
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0F172A', flex: 1 }}>
          {finding.test} <span style={{ fontFamily: 'Courier', color: sevColor }}>{finding.value}</span>
          {finding.reference_range && <span style={{ fontSize: 10, color: '#94A3B8', fontWeight: 500 }}> (ref {finding.reference_range})</span>}
        </div>
        <span style={{ ...badgeStyle(sevColor, sevColor + '20'), textTransform: 'uppercase' }}>{sev}</span>
        {expanded ? <ChevronUp size={14} color="#94A3B8" /> : <ChevronDown size={14} color="#94A3B8" />}
      </div>
      {expanded && (
        <>
          {finding.clinical_significance && <Field label="Clinical significance">{finding.clinical_significance}</Field>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10, marginBottom: 6 }}>
            {finding.trend && <Field label="Trend">{finding.trend}</Field>}
            {finding.recommended_followup && <Field label="Recommended follow-up">{finding.recommended_followup}</Field>}
          </div>
        </>
      )}
    </div>
  );
}

/* ─── Drug interaction card (Phase 2c) ──────────────────────────────────── */

function InteractionCard({ interaction, index }) {
  const [expanded, setExpanded] = useState(true);
  const sev = interaction.severity || 'unknown';
  const sevColor = sev === 'contraindicated' ? '#7F1D1D' :
                   sev === 'major' ? '#DC2626' :
                   sev === 'moderate' ? '#F59E0B' :
                   sev === 'minor' ? '#94A3B8' : '#475569';

  return (
    <div style={{ ...cardWrap, padding: 12, marginBottom: 10, borderLeft: `3px solid ${sevColor}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, cursor: 'pointer' }}
        onClick={() => setExpanded(!expanded)}>
        <Badge text={`#${index + 1}`} color="#F59E0B" />
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0F172A', flex: 1 }}>
          {interaction.drug_1} <span style={{ color: '#94A3B8' }}>×</span> {interaction.drug_2}
        </div>
        <span style={{ ...badgeStyle(sevColor, sevColor + '20'), textTransform: 'uppercase' }}>{sev}</span>
        {expanded ? <ChevronUp size={14} color="#94A3B8" /> : <ChevronDown size={14} color="#94A3B8" />}
      </div>
      {expanded && (
        <>
          {interaction.mechanism && <Field label="Mechanism">{interaction.mechanism}</Field>}
          {interaction.clinical_effect && <Field label="Clinical effect">{interaction.clinical_effect}</Field>}
          {interaction.management && <Field label="Management">{interaction.management}</Field>}
          {Array.isArray(interaction.supporting_sources) && interaction.supporting_sources.length > 0 && (
            <div>
              <FieldLbl>Sources cited (verify independently)</FieldLbl>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 11, color: '#475569' }}>
                {interaction.supporting_sources.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ─── Generic red/info list cards ───────────────────────────────────────── */

function RedListCard({ title, items }) {
  return (
    <div style={{ ...cardWrap, padding: 12, marginBottom: 12, background: '#FEF2F2', border: '1px solid #DC2626' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#991B1B', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 5 }}>
        <AlertTriangle size={12} /> {title}
      </div>
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: '#7F1D1D' }}>
        {items.map((m, i) => <li key={i}>{m}</li>)}
      </ul>
    </div>
  );
}

function InfoListCard({ title, items }) {
  return (
    <div style={{ ...cardWrap, padding: 12, marginBottom: 12, background: '#F0FDFA', border: '1px solid #14B8A6' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#0F766E', marginBottom: 6 }}>
        {title}
      </div>
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: '#134E4A' }}>
        {items.map((m, i) => <li key={i}>{m}</li>)}
      </ul>
    </div>
  );
}

function ReviewRow({ review }) {
  const actionMeta = {
    accepted: { color: '#15803D', bg: '#DCFCE7', label: 'Accepted', icon: CheckCircle },
    accepted_with_edits: { color: '#0F766E', bg: '#CCFBF1', label: 'Accepted with edits', icon: Edit2 },
    rejected: { color: '#DC2626', bg: '#FEE2E2', label: 'Rejected', icon: X },
    needs_more_info: { color: '#7C3AED', bg: '#F3E8FF', label: 'Needs more info', icon: AlertTriangle },
  }[review.action] || { color: '#475569', bg: '#F1F5F9', label: review.action, icon: FileText };
  const I = actionMeta.icon;

  return (
    <div style={{ ...cardWrap, padding: 10, marginBottom: 6, background: '#FAFBFC' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <I size={13} color={actionMeta.color} />
        <strong style={{ fontSize: 12, color: '#0F172A' }}>{actionMeta.label}</strong>
        <span style={{ fontSize: 10, color: '#94A3B8' }}>by {review.reviewer_name}</span>
        {review.reviewer_npi && (
          <span style={{ fontSize: 10, color: '#94A3B8' }}>NPI {review.reviewer_npi}</span>
        )}
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 10, color: '#94A3B8' }}>{new Date(review.reviewed_at).toLocaleString()}</span>
      </div>
      {review.notes && (
        <div style={{ fontSize: 12, color: '#0F172A', marginTop: 4 }}>{review.notes}</div>
      )}
      {review.edits && (
        <div style={{ fontSize: 11, color: '#475569', marginTop: 4, fontStyle: 'italic' }}>
          Edits: {review.edits}
        </div>
      )}
    </div>
  );
}

function ReviewForm({ requestId, onSubmitted, onCancel }) {
  const [action, setAction] = useState('accepted');
  const [notes, setNotes] = useState('');
  const [edits, setEdits] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    setSubmitting(true);
    try {
      const r = await window.zignagenixAPI.reviewClinicalAiRequest({
        requestId, action,
        notes: notes.trim() || null,
        edits: edits.trim() || null,
      });
      if (!r || r.success === false) {
        alert(r?.error || 'Review failed'); return;
      }
      onSubmitted();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ ...cardWrap, padding: 14, marginTop: 10, background: '#F8FAFC' }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#0F172A', marginBottom: 10 }}>
        Add physician review
      </div>
      <div style={{ marginBottom: 10 }}>
        <Lbl>Decision</Lbl>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[
            { v: 'accepted', label: 'Accept', color: '#15803D' },
            { v: 'accepted_with_edits', label: 'Accept w/ edits', color: '#0F766E' },
            { v: 'rejected', label: 'Reject', color: '#DC2626' },
            { v: 'needs_more_info', label: 'Needs more info', color: '#7C3AED' },
          ].map(a => (
            <button key={a.v} type="button" onClick={() => setAction(a.v)}
              style={{
                padding: '6px 12px', borderRadius: 999, border: 'none', cursor: 'pointer',
                fontSize: 12, fontWeight: 700,
                background: action === a.v ? a.color : '#F1F5F9',
                color: action === a.v ? '#fff' : '#475569',
              }}>{a.label}</button>
          ))}
        </div>
      </div>
      <div style={{ marginBottom: 10 }}>
        <Lbl>Notes (recommended for any reject / needs-more-info)</Lbl>
        <textarea rows={3} value={notes} onChange={e => setNotes(e.target.value)} style={{ ...inp, resize: 'vertical' }}
          placeholder="Why this decision? What did you verify independently? Any caveats for the patient's chart?" />
      </div>
      {action === 'accepted_with_edits' && (
        <div style={{ marginBottom: 10 }}>
          <Lbl>What you changed before using the suggestion</Lbl>
          <textarea rows={2} value={edits} onChange={e => setEdits(e.target.value)} style={{ ...inp, resize: 'vertical' }}
            placeholder="Specific changes — e.g. 'Lowered initial dose from 10⁶ to 5×10⁵ cells given patient's age and prior adverse reaction'." />
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button onClick={onCancel} style={btnSm}>Cancel</button>
        <button onClick={submit} disabled={submitting} style={btnPrimary}>
          {submitting ? 'Recording…' : 'Record review'}
        </button>
      </div>
    </div>
  );
}

/* ─── Helpers ──────────────────────────────────────────────────────────────── */

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <FieldLbl>{label}</FieldLbl>
      <div style={{ fontSize: 12, color: '#0F172A', marginTop: 2 }}>{children}</div>
    </div>
  );
}
function FieldLbl({ children }) {
  return (
    <span style={{ fontSize: 9, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase' }}>
      {children}
    </span>
  );
}
const Lbl = ({ children }) => (
  <label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>
    {children}
  </label>
);
const badgeStyle = (color, bg) => ({
  background: bg, color, fontSize: 10, fontWeight: 700,
  padding: '2px 8px', borderRadius: 999, textTransform: 'uppercase',
  whiteSpace: 'nowrap',
});
